import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { accessibleDivisionNames, canManageRecords } from "../lib/access";
import { fmtDateTime } from "../lib/timezone";
import { catMeta } from "../lib/advisories";
import AdvisoryMap from "../components/AdvisoryMap";
import AdvisoryForm from "../components/AdvisoryForm";
import { StatusBadge, CategoryBadge, UnmappedBadge } from "../components/AdvisoryBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PlusCircle, Search, MapIcon, List, AlertTriangle, ShieldCheck, CheckCircle2, ArrowRight, Clock, MapPin } from "lucide-react";

function Summary({ icon: Icon, label, value, tint, testid, onClick, active }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`flex items-center gap-3 rounded-md border bg-white p-4 text-left shadow-sm transition-colors ${active ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-md ${tint}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="font-display text-2xl font-black leading-none text-slate-900">{value}</p>
        <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
    </button>
  );
}

function AdvisoryItem({ a, selected, onSelect, onOpen, tz, refEl }) {
  const m = catMeta(a.category);
  const Icon = m.icon;
  return (
    <div
      ref={refEl}
      data-testid={`advisory-item-${a.id}`}
      onClick={() => onSelect(a)}
      className={`cursor-pointer rounded-md border p-3 transition-colors ${selected ? "border-blue-500 bg-blue-50/60 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md" style={{ background: `${m.color}1a`, color: m.color }}><Icon className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display font-bold text-slate-900" data-testid={`advisory-item-title-${a.id}`}>{a.title}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-600"><MapPin className="h-3 w-3 shrink-0" /> {a.location_description}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <StatusBadge status={a.effective_status} />
            <CategoryBadge category={a.category} />
            {!a.has_location && <UnmappedBadge />}
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{a.visibility === "org" ? "Agency-wide" : a.division}</span>
          </div>
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500">
            <Clock className="h-3 w-3" />
            {a.effective_status === "resolved" ? `Resolved ${fmtDateTime(a.resolved_at, tz)}` : `Review by ${fmtDateTime(a.next_review_at, tz)}`}
            {" · "}{a.last_verified_at ? `Verified by ${a.last_verified_by_name}` : "Not yet reverified"}
          </p>
        </div>
        <Button size="sm" variant="ghost" data-testid={`advisory-open-${a.id}`} onClick={(e) => { e.stopPropagation(); onOpen(a); }} className="shrink-0 gap-1 text-blue-700">Details <ArrowRight className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

export default function Advisories() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tz = user?.org_timezone;
  const [config, setConfig] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [f, setF] = useState({ status: params.get("status") || "unresolved", category: "all", division: "all", q: "" });
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [showForm, setShowForm] = useState(false);
  const itemRefs = useRef({});

  const load = useCallback(async () => {
    const [l, s] = await Promise.all([
      api.get("/advisories", { params: { status: f.status, category: f.category, division: f.division, q: f.q || undefined } }),
      api.get("/advisories/summary"),
    ]);
    setItems(l.data);
    setSummary(s.data);
  }, [f]);

  useEffect(() => {
    api.get("/advisories/config").then((r) => setConfig(r.data)).catch(() => setConfig({ map_enabled: false, categories: [] }));
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load().catch(() => setItems([])); }, [load]);

  const divNames = useMemo(() => accessibleDivisionNames(user, divisions.filter((d) => d.active !== false)), [user, divisions]);
  const setFilter = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const select = (a, fromMap) => {
    setSelected(a.id);
    if (a.has_location) setFocus({ lat: a.lat, lng: a.lng, id: a.id });
    if (fromMap) itemRefs.current[a.id]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const canCreate = !!user; // employees create in own division; backend enforces

  return (
    <div className="space-y-6 animate-fade-up" data-testid="advisories-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Persistent Conditions</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Operational Advisories</h1>
          <p className="mt-1 text-sm text-slate-500">Road closures, flooding and restrictions that stay visible across shifts until someone confirms they're resolved.</p>
        </div>
        {canCreate && (
          <Button data-testid="add-advisory-button" onClick={() => setShowForm(true)} className="gap-2 bg-blue-600 text-white hover:bg-blue-700"><PlusCircle className="h-4 w-4" /> Add Advisory</Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Summary testid="summary-active" icon={ShieldCheck} label="Active (incl. needs verification)" value={summary?.unresolved ?? "—"} tint="bg-blue-50 text-blue-600" active={f.status === "unresolved"} onClick={() => setFilter("status")("unresolved")} />
        <Summary testid="summary-needs-verification" icon={AlertTriangle} label="Needs verification" value={summary?.needs_verification ?? "—"} tint="bg-amber-50 text-amber-600" active={f.status === "needs_verification"} onClick={() => setFilter("status")("needs_verification")} />
        <Summary testid="summary-recently-resolved" icon={CheckCircle2} label="Resolved (last 7 days)" value={summary?.recently_resolved ?? "—"} tint="bg-slate-100 text-slate-600" active={f.status === "resolved"} onClick={() => setFilter("status")("resolved")} />
      </div>

      <Card className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid="advisory-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="advisory-search-input" value={f.q} onChange={(e) => setFilter("q")(e.target.value)} placeholder="Search title, location, notes…" className="h-9 pl-8 text-sm" />
          </div>
          <Select value={f.status} onValueChange={setFilter("status")}>
            <SelectTrigger data-testid="advisory-filter-status" className="h-9 w-full text-xs sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unresolved">Unresolved (default)</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="needs_verification">Needs verification</SelectItem>
              <SelectItem value="resolved">Resolved (history)</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={f.category} onValueChange={setFilter("category")}>
            <SelectTrigger data-testid="advisory-filter-category" className="h-9 w-full text-xs sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(config?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.division} onValueChange={setFilter("division")}>
            <SelectTrigger data-testid="advisory-filter-division" className="h-9 w-full text-xs sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All my divisions</SelectItem>
              {divNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-slate-200 p-0.5 lg:hidden" data-testid="advisory-view-toggle">
            <button type="button" data-testid="advisory-view-list" onClick={() => setMobileView("list")} className={`flex flex-1 items-center justify-center gap-1 rounded px-3 py-1.5 text-xs font-semibold ${mobileView === "list" ? "bg-slate-900 text-white" : "text-slate-600"}`}><List className="h-3.5 w-3.5" /> List</button>
            <button type="button" data-testid="advisory-view-map" onClick={() => setMobileView("map")} className={`flex flex-1 items-center justify-center gap-1 rounded px-3 py-1.5 text-xs font-semibold ${mobileView === "map" ? "bg-slate-900 text-white" : "text-slate-600"}`}><MapIcon className="h-3.5 w-3.5" /> Map</button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className={`lg:col-span-3 ${mobileView === "map" ? "hidden lg:block" : ""}`} data-testid="advisory-map-panel">
          <div className="h-[420px] lg:sticky lg:top-20 lg:h-[calc(100vh-11rem)]">
            {config ? (
              <AdvisoryMap config={config} advisories={items || []} selectedId={selected} onSelect={(a) => select(a, true)} focus={focus} className="h-full" />
            ) : <div className="h-full animate-pulse rounded-md bg-slate-200/70" />}
          </div>
          {config?.map_enabled && (
            <p className="mt-1 text-[11px] text-slate-400">Colored pins = category · dashed pins need verification · advisories without a pin appear in the list only.</p>
          )}
        </div>
        <div className={`space-y-2 lg:col-span-2 ${mobileView === "list" ? "" : "hidden lg:block"}`} data-testid="advisory-list">
          {items === null ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-md bg-slate-200/70" />)
          ) : items.length === 0 ? (
            <Card className="rounded-md border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="advisory-empty">No advisories match these filters.</Card>
          ) : (
            items.map((a) => (
              <AdvisoryItem key={a.id} a={a} tz={tz} selected={a.id === selected} onSelect={(x) => select(x, false)} onOpen={(x) => navigate(`/advisories/${x.id}`)} refEl={(el) => { itemRefs.current[a.id] = el; }} />
            ))
          )}
        </div>
      </div>

      {canManageRecords(user) ? null : null}
      <AdvisoryForm open={showForm} onClose={() => setShowForm(false)} onSaved={() => load()} config={config} divisions={divisions} />
    </div>
  );
}
